import { useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { WritableAtom, useAtom } from 'jotai';

/**
 * FullscreenModal
 * - Renders children in a fullscreen modal
 * - Mounts to __PintarStudiosModalMount if available, else document.body
 * - Opens/closes based on a jotai atom
 * - Syncs with URL (pathSegment argument, e.g. "panorama")
 */
export const FullscreenModal = ({
  isOpenAtom,
  pathSegment,
  children,
  className,
}: {
  isOpenAtom: WritableAtom<boolean, [boolean], void>; // jotai WritableAtom<boolean, [boolean], void>
  pathSegment: string; // e.g. "panorama"
  className?: string;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useAtom(isOpenAtom);

  // Sync with browser history
  useEffect(() => {
    if (isOpen) {
      window.history.pushState({ modal: pathSegment }, '', `#${pathSegment}`);
    }

    const handlePopState = () => {
      // If history changes and modal was open, close it
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, pathSegment, setIsOpen]);

  const closeModal = () => {
    setIsOpen(false);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (window.history.state?.modal === pathSegment) {
      window.history.back();
    }
  };

  // Lock background scroll so the host scrollbar disappears
  useLayoutEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    const body = document.body;

    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlPaddingRight = html.style.paddingRight;

    // width of the scrollbar (to avoid layout shift on the host page)
    const sbw = window.innerWidth - html.clientWidth;

    html.style.setProperty('overflow', 'hidden', 'important');
    body.style.setProperty('overflow', 'hidden', 'important');
    html.style.setProperty('overscroll-behavior', 'contain', 'important');
    body.style.setProperty('overscroll-behavior', 'contain', 'important');
    if (sbw > 0) {
      html.style.setProperty('padding-right', `${sbw}px`, 'important');
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.paddingRight = prevHtmlPaddingRight;
      html.style.removeProperty('overscroll-behavior');
      body.style.removeProperty('overscroll-behavior');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalShadow = window.__PintarStudiosModalMount;
  const renderTarget = modalShadow ? modalShadow : document.body;

  return createPortal(
    <div
      id={`pintarstudios-${pathSegment}-modal`}
      className="fixed inset-0 z-[2147483647] flex items-center justify-center"
    >
      {/* dark background overlay */}
      <div tabIndex={0} role="button" className="absolute inset-0 cursor-default bg-black/90" onClick={closeModal} />

      {/* content box */}
      <div className={`${className ? className : ''}`}>
        {children}
        <button
          onClick={closeModal}
          className="absolute left-1/2 top-3 z-[9999] -translate-x-1/2 rounded bg-black/70 px-3 py-1 text-white hover:bg-black/90 md:top-4 md:px-4 md:py-2 md:text-2xl"
        >
          ✕
        </button>
      </div>
    </div>,
    renderTarget
  );
};
